import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import { PasswordRecoveryBox } from '@/features/auth/PasswordRecoveryBox';
import {
  fetchMember,
  inviteMember,
  issueMemberTemporaryPassword,
  sendMemberPasswordResetEmail,
} from '@/features/members/api';
import { MemberMeetEmailsBox } from '@/features/members/MemberMeetEmailsBox';
import { MemberRelatedNav } from '@/features/members/MemberRelatedNav';
import { ChannelPrefsBox } from '@/features/notifications/ChannelPrefsBox';
import { fetchMemberChannelPrefs, updateMemberChannelPref } from '@/features/notifications/api';
import { ApiError } from '@/lib/api/http';

/**
 * 会員の連携設定編集（事務局のみ / D-143）。
 * Meet用メール・通知の受け取り方（Slack・Chatwork）・ログイン情報（パスワード復旧・
 * 未紐づけ会員の招待）という「プロフィールとは別の、外部連携・アカウントまわり」を
 * ここにまとめる。基本情報・写真・在籍状態の変更は「プロフィール編集」
 * （`MemberEditPage`）に分けた。
 */
export function MemberIntegrationsEditPage(): ReactElement {
  const { memberId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['member', memberId],
    queryFn: () => fetchMember(memberId),
    enabled: Boolean(memberId),
  });

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    return forbidden ? (
      <ForbiddenState />
    ) : (
      <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
    );
  }

  const member = query.data;

  async function onInviteMember(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await inviteMember(member.id, inviteEmail);
      setInviteEmail('');
      await queryClient.invalidateQueries({ queryKey: ['member', memberId] });
      setNotice(`${inviteEmail} に招待メールを送りました。`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '招待に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title={`${member.name}さんの連携設定編集`}
        description={`会員ID: ${member.member_id}`}
      />
      <MemberRelatedNav memberId={member.id} current="integrations" />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {/* Meet突合用メール: 事務局のみ（D-063） */}
      <MemberMeetEmailsBox memberId={member.id} />

      {/* Slack・Chatworkの宛先（事務局による代理登録 / D-106）。アカウント未紐づけの間は出さない */}
      {member.user_id && (
        <ChannelPrefsBox
          queryKey={['channel-prefs', member.id]}
          fetchPrefs={() => fetchMemberChannelPrefs(member.id)}
          updatePref={(input) => updateMemberChannelPref(member.id, input)}
          officeMode
        />
      )}

      {/* パスワードを紛失した受講生の救済・未紐づけ会員の招待: 事務局のみ（D-082・D-140） */}
      <div className="mt-4">
        <MetaBox title="ログイン情報">
          {member.user_id ? (
            <PasswordRecoveryBox
              // 再設定メールはログインID（User.email）宛に届く。
              // 連絡先メール（contact_email）を出していたのは誤り（D-178）
              sendTo={member.login_email ?? 'ログイン用のメールアドレス'}
              onSendResetEmail={() => sendMemberPasswordResetEmail(member.id)}
              onIssueTemporary={() => issueMemberTemporaryPassword(member.id)}
            />
          ) : (
            <>
              <p className="mb-3 text-wptext-muted">
                この会員にはログインアカウントが紐づいていません。招待メールを送ると、
                本人が受諾した時点でこの会員に自動で紐づきます。
              </p>
              <label className="mb-3 block">
                <span className="mb-1.5 block text-wptext-muted">招待先のメールアドレス</span>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full max-w-sm rounded-sm border border-wpborder px-3 py-2"
                />
              </label>
              <button
                type="button"
                disabled={busy || !inviteEmail}
                onClick={() => void onInviteMember()}
                className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
              >
                招待する
              </button>
            </>
          )}
        </MetaBox>
      </div>

      <button
        type="button"
        onClick={() => navigate(`/members/${member.id}`)}
        className="mt-4 text-wpaccent hover:underline"
      >
        ← 会員詳細に戻る
      </button>
    </>
  );
}
